import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Custom hook to track window width for responsiveness
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const BecomePartner = () => {
  const width = useWindowWidth();

  // Breakpoints
  const isMobile = width < 700;
  const isTablet = width >= 700 && width < 1200;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    organizationInstitution: '',
    partnershipType: '',
    goals: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.organizationInstitution || !formData.partnershipType) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New Partnership Inquiry
      -------------------------

      Inquirer Details:
      - Name: ${formData.firstName} ${formData.lastName}
      - Email: ${formData.email}
      - Phone: ${formData.phoneNumber || 'Not provided'}
      - Organization/Institution: ${formData.organizationInstitution}

      Partnership Information:
      - Type of Partnership: ${formData.partnershipType}
      - Goals & Requirements:
      ${formData.goals || 'No specific goals were provided.'}
    `;

    const templateParams = {
      email_subject: `New ${formData.partnershipType} Partnership Inquiry`,
      email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your partnership inquiry! Our team will get in touch with you shortly.');
        setIsSubmitting(false);
        setFormData({
            firstName: '', lastName: '', email: '', phoneNumber: '',
            organizationInstitution: '', partnershipType: '', goals: ''
        });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('An error occurred while sending your inquiry. Please try again later.');
        setIsSubmitting(false);
      });
  };

  // Styles with responsiveness
  const styles = {
    fontFamily: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    heroSection: {
      background:
        'linear-gradient(135deg, #1a0b3d 0%, #2d1b69 25%, #4a3ba1 50%, #6b5ce7 75%, #8a7ff0 100%)',
      minHeight: isMobile ? '70vh' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '40px 15px 40px' : '0'
    },
    backgroundPatterns: {
      position: 'absolute',
      zIndex: 1,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      backgroundImage: `
        radial-gradient(circle at 15% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
        radial-gradient(circle at 85% 75%, rgba(255,255,255,0.05) 0%, transparent 50%),
        radial-gradient(circle at 25% 85%, rgba(255,255,255,0.08) 0%, transparent 40%),
        radial-gradient(circle at 75% 15%, rgba(255,255,255,0.06) 0%, transparent 35%)
      `
    },
    networkLines: {
      position: 'absolute',
      zIndex: 1,
      top: '20%',
      right: isMobile ? '5%' : '10%',
      width: isMobile ? '150px' : '300px',
      height: isMobile ? '150px' : '300px',
      backgroundImage: `
        linear-gradient(45deg, transparent 49%, rgba(255,255,255,0.1) 50%, transparent 51%),
        linear-gradient(-45deg, transparent 49%, rgba(255,255,255,0.1) 50%, transparent 51%)
      `,
      backgroundSize: '20px 20px'
    },
    dottedPattern: {
      position: 'absolute',
      zIndex: 1,
      bottom: '15%',
      left: isMobile ? '3%' : '5%',
      width: isMobile ? '100px' : '200px',
      height: isMobile ? '100px' : '200px',
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.2) 2px, transparent 2px)',
      backgroundSize: '15px 15px'
    },
    heroContent: {
      position: 'relative',
      zIndex: 2,
      maxWidth: isMobile ? '95vw' : '900px',
      padding: isMobile ? '0 10px' : '0 20px'
    },
    heroTitle: {
      fontSize: isMobile ? '3rem' : isTablet ? '5rem' : '6rem',
      fontWeight: '700',
      margin: '0 0 30px 0',
      lineHeight: 1.1,
      letterSpacing: '-2px',
      wordBreak: 'break-word'
    },
    heroParagraph: {
      fontSize: isMobile ? '1rem' : '1.25rem',
      marginBottom: '50px',
      color: 'rgba(255, 255, 255, 0.9)',
      maxWidth: isMobile ? '95vw' : '700px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: 1.5
    },
    highlightText: {
      color: '#8a7ff0',
      fontWeight: '600'
    },
    heroButtonWrapper: {
      display: 'flex',
      gap: '20px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    heroButton: {
      backgroundColor: '#2d1b69',
      color: 'white',
      border: 'none',
      padding: isMobile ? '12px 24px' : '15px 30px',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    // Partnership Opportunities Section
    opportunitiesSection: {
      backgroundColor: '#f8f9fa',
      padding: isMobile ? '60px 15px' : '120px 20px',
      textAlign: 'center'
    },
    opportunitiesContainer: {
      maxWidth: isMobile ? '95vw' : '1200px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    opportunitiesTitle: {
      fontSize: isMobile ? '2.5rem' : '3.5rem',
      fontWeight: '700',
      color: '#2d1b69',
      marginBottom: isMobile ? '15px' : '20px'
    },
    opportunitiesDescription: {
      fontSize: isMobile ? '1rem' : '1.125rem',
      color: '#666',
      marginBottom: isMobile ? '40px' : '100px',
      maxWidth: isMobile ? '95vw' : '600px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    opportunityCardsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile
        ? '1fr'
        : isTablet
        ? '1fr 1fr'
        : 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: isMobile ? '30px' : '40px'
    },
    opportunityCard: {
      backgroundColor: 'white',
      padding: isMobile ? '30px 20px' : '50px 40px',
      borderRadius: '16px',
      textAlign: 'center',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(45, 27, 105, 0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      userSelect: 'none'
    },
    iconWrapper: {
      width: isMobile ? '60px' : '80px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 30px auto',
      fontSize: isMobile ? '24px' : '32px',
      color: 'white'
    },
    iconColors: {
      student: '#8a7ff0',
      institution: '#00b894',
      corporate: '#e74c3c'
    },
    opportunityTitle: {
      fontSize: isMobile ? '1.8rem' : '2rem',
      fontWeight: '700',
      color: '#2d1b69',
      marginBottom: '20px'
    },
    opportunityDescription: {
      fontSize: isMobile ? '0.95rem' : '1rem',
      color: '#666',
      marginBottom: '30px',
      lineHeight: 1.6
    },
    keyAdvantagesTitle: {
      fontSize: isMobile ? '1.1rem' : '1.25rem',
      fontWeight: '600',
      color: '#2d1b69',
      marginBottom: '20px',
      textAlign: 'left'
    },
    keyAdvantagesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      textAlign: 'left'
    },
    keyAdvantageItem: (color) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      fontSize: '14px',
      color: '#333',
      lineHeight: 1.4,
      userSelect: 'none'
    }),
    keyAdvantageIcon: (color) => ({
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: color,
      color: 'white',
      fontSize: '12px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '2px',
      flexShrink: 0
    }),
    // Partnership Form Section
    formSection: {
      backgroundColor: 'white',
      padding: isMobile ? '60px 15px' : '120px 20px',
      textAlign: 'center'
    },
    formContainer: {
      maxWidth: isMobile ? '95vw' : '800px',
      marginLeft: 'auto',
      marginRight: 'auto',
      boxShadow: isMobile ? 'none' : '0 12px 48px rgba(0, 0, 0, 0.1)',
      backgroundColor: '#f8f9fa',
      padding: isMobile ? '20px 16px' : '60px',
      borderRadius: '20px'
    },
    formIconWrapper: {
      width: '80px',
      height: '80px',
      backgroundColor: '#2d1b69',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 30px auto',
      fontSize: '32px',
      color: 'white'
    },
    formTitle: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: '700',
      color: '#2d1b69',
      marginBottom: isMobile ? '25px' : '40px'
    },
    formDescription: {
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: '#666',
      marginBottom: isMobile ? '40px' : '60px',
      maxWidth: isMobile ? '95vw' : '600px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: 1.5
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '20px',
      marginBottom: '24px'
    },
    formLabel: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '8px',
      textAlign: 'left'
    },
    input: {
      width: '100%',
      padding: isMobile ? '12px' : '16px',
      border: '1px solid #ddd',
      borderRadius: '12px',
      fontSize: '16px',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      transition: 'border-color 0.3s ease',
      outline: 'none'
    },
    select: {
      width: '100%',
      padding: isMobile ? '12px' : '16px',
      border: '1px solid #ddd',
      borderRadius: '12px',
      fontSize: '16px',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      transition: 'border-color 0.3s ease',
      outline: 'none',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '12px' : '16px',
      border: '1px solid #ddd',
      borderRadius: '12px',
      fontSize: '16px',
      backgroundColor: 'white',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      transition: 'border-color 0.3s ease',
      outline: 'none',
      minHeight: isMobile ? '100px' : '140px'
    },
    submitButton: {
      width: '100%',
      backgroundColor: '#2d1b69',
      color: 'white',
      border: 'none',
      padding: isMobile ? '15px' : '20px',
      borderRadius: '12px',
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px'
    }
  };

  // Construct opportunity cards data for easier mapping
  const opportunityCards = [
    {
      id: 'student',
      icon: '🎓',
      color: styles.iconColors.student,
      title: 'Student Partner',
      description:
        'Join our student ambassador program and lead AI education in your campus',
      advantages: [
        'Exclusive access to premium AI courses and certifications',
        'Leadership opportunities in organizing AI events and workshops',
        'Global networking with fellow student ambassadors',
        'Early career opportunities and internship placements'
      ]
    },
    {
      id: 'institution',
      icon: '🏛️',
      color: styles.iconColors.institution,
      title: 'Institution Partner',
      description: 'Partner with us to integrate AI education into your academic curriculum',
      advantages: [
        'Customized AI curriculum development for your programs',
        'Faculty training and professional development workshops',
        'Joint certification programs with industry recognition',
        'Access to cutting-edge AI research and resources'
      ]
    },
    {
      id: 'corporate',
      icon: '💼',
      color: styles.iconColors.corporate,
      title: 'Corporate Partner',
      description: 'Collaborate with us to build AI talent pipeline and drive innovation',
      advantages: [
        'Direct access to trained AI talent for recruitment',
        'Custom training programs for your workforce',
        'Joint research projects and innovation initiatives',
        'Brand visibility in global AI education community'
      ]
    }
  ];

  return (
    <div style={styles.fontFamily}>
      <Navbar />
      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div style={styles.backgroundPatterns} />
        <div style={styles.networkLines} />
        <div style={styles.dottedPattern} />

        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            Become a Partner
          </h1>
          <p style={styles.heroParagraph}>
            Join our <span style={styles.highlightText}>global network</span> and help shape the future of AI education.
            <br />
            Choose the partnership that aligns with your <span style={styles.highlightText}>goals and impact</span>.
          </p>
          <div style={styles.heroButtonWrapper}>
            <a href="#form-section" style={styles.heroButton}>🔗 Explore Partnerships</a>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities Section */}
      <section style={styles.opportunitiesSection}>
        <div style={styles.opportunitiesContainer}>
          <h2 style={styles.opportunitiesTitle}>Partnership Opportunities</h2>
          <p style={styles.opportunitiesDescription}>
            Discover how you can contribute to and benefit from our mission to{' '}
            <span style={{ fontWeight: '600', color: '#2d1b69' }}>
              democratize AI education
            </span>{' '}
            globally.
          </p>
          <div style={styles.opportunityCardsGrid}>
            {opportunityCards.map(({ id, icon, color, title, description, advantages }) => (
              <div key={id} style={styles.opportunityCard}>
                <div style={{ ...styles.iconWrapper, backgroundColor: color }}>{icon}</div>
                <h3 style={styles.opportunityTitle}>{title}</h3>
                <p style={styles.opportunityDescription}>{description}</p>
                <div style={styles.keyAdvantagesTitle}>🔑 Key Advantages:</div>
                <div style={styles.keyAdvantagesList}>
                  {advantages.map((adv, idx) => (
                    <div key={idx} style={styles.keyAdvantageItem(color)}>
                      <div style={styles.keyAdvantageIcon(color)}>✓</div>
                      <span>{adv}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Inquiry Form Section */}
      <section id="form-section" style={styles.formSection}>
        <div style={styles.formContainer}>
          <div style={styles.formIconWrapper}>⭐</div>
          <h2 style={styles.formTitle}>Partnership Inquiry</h2>
          <p style={styles.formDescription}>
            Fill out the form below and our partnership team will get back to you within{' '}
            <span style={{ color: '#2d1b69', fontWeight: '600' }}>24 hours</span>.
          </p>

          <form onSubmit={handleSubmit} autoComplete="off">
            <div style={styles.formGrid}>
              <div>
                <label style={styles.formLabel}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <label style={styles.formLabel}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formGrid}>
              <div>
                <label style={styles.formLabel}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <label style={styles.formLabel}>Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGrid}>
              <div>
                <label style={styles.formLabel}>Organization / Institution</label>
                <input
                  type="text"
                  name="organizationInstitution"
                  value={formData.organizationInstitution}
                  onChange={handleInputChange}
                  placeholder="Enter organization name"
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <label style={styles.formLabel}>Partnership Type</label>
                <select
                  name="partnershipType"
                  value={formData.partnershipType}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Select partnership type</option>
                  <option value="student">Student Partner</option>
                  <option value="institution">Institution Partner</option>
                  <option value="corporate">Corporate Partner</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '40px', textAlign: 'left' }}>
              <label style={styles.formLabel}>Tell us about your goals</label>
              <textarea
                name="goals"
                value={formData.goals}
                onChange={handleInputChange}
                placeholder="Describe how you'd like to partner with Global AI Society and what you hope to achieve..."
                rows={isMobile ? 5 : 6}
                style={styles.textarea}
              />
            </div>

            <button type="submit" style={styles.submitButton} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : '🚀 Submit Partnership Inquiry →'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomePartner;
